import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth } from '../firebase-config';

/**
 * Firebase Functions Service Layer
 * Centralizes all Cloud Function calls with error handling
 */

// Default region for functions
const FUNCTIONS_REGION = 'us-central1';

// Get functions instance
const getFunctionsInstance = () => {
  if (!auth?.app) {
    throw new Error('Firebase not initialized');
  }
  return getFunctions(auth.app, FUNCTIONS_REGION);
};

/**
 * Call a Cloud Function with error handling
 * @param {string} functionName 
 * @param {object} data 
 */
const callFunction = async (functionName, data = {}) => {
  try {
    const functions = getFunctionsInstance();
    const callable = httpsCallable(functions, functionName);
    const result = await callable(data);
    return result.data;
  } catch (error) {
    console.error(`Error calling ${functionName}:`, error);
    
    // Handle specific error codes
    if (error.code === 'functions/not-found') {
      throw new Error('Cloud Functions not deployed. Please deploy functions first.');
    }
    if (error.code === 'functions/unauthenticated') {
      throw new Error('Authentication required');
    }
    if (error.code === 'functions/invalid-argument') {
      throw new Error(error.message || 'Invalid request data');
    }
    if (error.code === 'functions/resource-exhausted') {
      throw new Error('Too many requests. Please try again later.');
    }
    
    // Re-throw with cleaner message
    throw new Error(error.message || 'Function call failed');
  }
};

/**
 * Request password reset email
 * @param {string} username 
 */
export const requestPasswordReset = async (username) => {
  if (!username) {
    throw new Error('Username is required');
  }
  
  return callFunction('requestPasswordReset', { 
    username: username.trim().toLowerCase() 
  });
};

/**
 * Reset password with token
 * @param {string} token 
 * @param {number} newPassword 
 */
export const resetPassword = async (token, newPassword) => {
  if (!token) {
    throw new Error('Reset token is required');
  }
  
  const passwordNum = parseInt(newPassword);
  if (!passwordNum || passwordNum < 1 || passwordNum > 100) {
    throw new Error('Password must be a number between 1 and 100');
  }
  
  return callFunction('resetPassword', { token, newPassword: passwordNum });
};

/**
 * Validate reset token
 * @param {string} token 
 */
export const validateResetToken = async (token) => {
  if (!token) {
    throw new Error('Reset token is required');
  }
  
  return callFunction('validateResetToken', { token });
};

/**
 * Increment score (server-side validation).
 * Strike/ban state is read server-side from /bans/{uid} - no longer trusted from client.
 */
export const incrementScore = async (amount, username, isGuest, sessionData = {}) => {
  if (!amount || amount < 1) {
    throw new Error('Invalid increment amount');
  }

  return callFunction('incrementScore', {
    amount,
    username,
    isGuest,
    sessionData
  });
};

/**
 * Update user's recovery email.
 * Changing or removing an existing email requires confirming the current one;
 * the server never reveals the existing address, so only the real owner can supply it.
 * @param {string} username
 * @param {string|null} oldEmail - The user's current recovery email, or null if none is set
 * @param {string|null} newEmail - The new address, or null to remove
 */
export const updateUserEmail = async (username, oldEmail, newEmail) => {
  if (!username) {
    throw new Error('Username is required');
  }

  return callFunction('updateUserEmail', {
    username: username.trim().toLowerCase(),
    oldEmail: oldEmail || null,
    newEmail: newEmail || null
  });
};

/**
 * Request the 9 login number options for a username.
 * Returns { exists, isLocked, remainingSeconds?, numbers?, requiresPasswordSetup? }.
 */
export const requestLoginOptions = async (username) => {
  if (!username) throw new Error('Username is required');
  return callFunction('requestLoginOptions', { username: username.trim().toLowerCase() });
};

/**
 * Verify a login attempt server-side.
 * Returns { success, hasEmail?, isLocked?, remainingSeconds?, attemptsRemaining? }.
 */
export const loginUser = async (username, selectedNumber, rememberDevice = false) => {
  if (!username) throw new Error('Username is required');
  if (!Number.isInteger(selectedNumber)) throw new Error('Invalid selection');
  return callFunction('loginUser', {
    username: username.trim().toLowerCase(),
    selectedNumber,
    rememberDevice: Boolean(rememberDevice)
  });
};

/**
 * Get minimal account status for the logged-in user.
 * Returns { hasEmail: boolean } - never the actual email address.
 */
export const getAccountStatus = async () => {
  return callFunction('getAccountStatus', {});
};

/**
 * Update the rememberDevice preference for the current account session.
 */
export const setRememberDevice = async (remember) => {
  return callFunction('setRememberDevice', { remember: Boolean(remember) });
};

/**
 * Clear the current device's session (used when switching to guest play).
 */
export const clearDeviceSession = async () => {
  return callFunction('clearDeviceSession', {});
};

/**
 * Change username
 * @param {string} oldUsername - Current username
 * @param {string} newUsername - Desired new username
 */
export const changeUsername = async (oldUsername, newUsername) => {
  if (!oldUsername || !newUsername) {
    throw new Error('Both old and new usernames are required');
  }

  return callFunction('changeUsername', {
    oldUsername: oldUsername.trim().toLowerCase(),
    newUsername: newUsername.trim().toLowerCase()
  });
};

/**
 * Unban a user (admin only)
 * @param {string} uid - User ID to unban
 * @param {string} adminPassword - Admin password for authentication
 */
export const unbanUser = async (uid, adminPassword) => {
  if (!uid) {
    throw new Error('User ID is required');
  }
  
  if (!adminPassword) {
    throw new Error('Admin password is required');
  }
  
  return callFunction('unbanUser', { uid, adminPassword });
};

// Export utility to check if functions are available
export const areFunctionsAvailable = () => {
  try {
    getFunctionsInstance();
    return true;
  } catch {
    return false;
  }
};